import { useCallback, useEffect } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
  faTags,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import { inputsFiltersProducts } from './filters/filters-products'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import { maskInteger } from '../../../utils/maskUtils'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'

const ProductsPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      'items.condition.name': [],
      'items.category.name': [],
      'items.address.street': [],
      'items.address.alias': [],
      'items.address.number': [],
      'items.address.city': [],
      'items.address.state': [],
      deleted_at: false,
    },
    'almox_products'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        'items.condition.name': [],
        'items.category.name': [],
        'items.address.street': [],
        'items.address.alias': [],
        'items.address.number': [],
        'items.address.city': [],
        'items.address.state': [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const {
    data: productsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['almox_products', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'id',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(entities.products.get, params)
      return response.result
    },
    staleTime: 60 * 1000,
    select: (data) => ({
      products: data.data
        .filter((product) => {
          // FILTRO: Só mantém produtos que têm pelo menos 1 item no estoque
          const totalEstoque = (product.items || []).reduce(
            (acc, item) => acc + (Number(item?.quantity) || 0),
            0
          )
          return totalEstoque > 0
        })
        .map((product) => {
          const items = product.items || []

          const totalEstoque = items.reduce(
            (acc, item) => acc + (Number(item?.quantity) || 0),
            0
          )
          const totalCusto = items.reduce(
            (acc, item) =>
              acc +
              (Number(item?.average_cost) || 0) * (Number(item?.quantity) || 0),
            0
          )
          const preco_medio = totalEstoque > 0 ? totalCusto / totalEstoque : 0

          return {
            id: product.id,
            name: product.name || '-',
            preco_medio,
            estoque: totalEstoque,
            items: items,
            is_in_transaction: product.is_in_transaction,
            is_asset: product.is_asset,
            categorias: items.map((item) => ({
              code: item?.code || '-',
              endereco: item?.address?.formatted_address || '-',
              localizacao: item?.location?.formatted_location || '-',
              categoria: item?.category?.name || '-',
              condicao: item?.condition?.name || '-',
              preco_medio: Number(item?.average_cost) || 0,
              estoque: maskInteger(Number(item?.quantity)) || 0,
            })),
            isDeleted: !!product.deleted_at,
          }
        }),
      totalPages: data.last_page,
    }),
    placeholderData: (prev) => prev,
  })

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      console.error('Erro ao carregar produtos:', error)
      showNotification(
        'error',
        `Erro ao carregar produtos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries([
      'almox_products',
      transformFiltersForUrl(filters),
      pagination,
    ])
  }, [queryClient, filters, pagination])

  const inactivateProductMutation = useMutation({
    mutationFn: async (id) => {
      showLoader()
      return await baseServiceDelete(entities.products.delete(id))
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList()
    },
    onError: (err) => {
      console.log('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activateProductMutation = inactivateProductMutation

  const exportTags = useMutation({
    mutationFn: async ({ productId }) => {
      showLoader()
      const response = await baseServiceGet(
        entities.reports.tagProduct(productId),
        {}
      )
      return response.result
    },
    onSuccess: async (resultData) => {
      let { check_status_url } = resultData

      // 🛠️ CORREÇÃO DEFINITIVA: Remove qualquer "/api" do início da URL
      // para evitar que o baseService duplique o prefixo e cause o erro 404.
      if (check_status_url) {
        check_status_url = check_status_url.replace(/^\/?api\//, '')
      }

      let attempts = 0
      const maxAttempts = 30 // 60 segundos de timeout

      const checkStatus = async () => {
        try {
          attempts++

          const statusResponse = await baseServiceGet(check_status_url, {})

          const currentStatus = statusResponse.result?.status
          const isReady =
            currentStatus === 'completed' || currentStatus === 'success'
          const hasFailed =
            currentStatus === 'failed' || currentStatus === 'cancelled'

          if (isReady) {
            const filePath = statusResponse.result?.file_path

            if (filePath) {
              // Monta a URL correta
              let directUrl = filePath
              if (!filePath.startsWith('http')) {
                const cleanPath = filePath.replace(/^(storage\/|public\/)/, '')
                directUrl = `${window.location.origin}/storage/${cleanPath}`
              }

              // Abre o PDF diretamente numa nova aba (evita CORS)
              window.open(directUrl, '_blank')

              showNotification('success', 'Etiquetas geradas com sucesso!')
            }

            clearInterval(pollInterval)
            hideLoader()
          } else if (hasFailed) {
            clearInterval(pollInterval)
            hideLoader()
            showNotification('error', 'Falha ao gerar as etiquetas.')
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval)
            hideLoader()
            showNotification(
              'warning',
              'A geração está demorando mais que o esperado.'
            )
          }
        } catch (error) {
          console.error('Erro ao verificar status:', error)
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval)
            hideLoader()
            showNotification(
              'error',
              'Erro ao processar download: ' + error.message
            )
          }
        }
      }

      const pollInterval = setInterval(checkStatus, 2000)
      checkStatus()
    },
    onError: (error) => {
      console.error('Erro ao iniciar exportação:', error)
      showNotification('error', 'Erro ao iniciar geração das etiquetas.')
      hideLoader()
    },
  })

  const actions = [
    {
      id: 'exportar_etiquetas',
      icon: faTags,
      title: 'Imprimir Etiquetas',
      buttonClass: 'btn-success',
      permission: 'Ver produtos',
      onClick: async (product) => {
        try {
          await exportTags.mutateAsync({ productId: product.id })
        } catch (error) {
          console.log('Erro ao exportar etiquetas:', {
            status: error.status,
            message: error.message,
          })
        }
      },
      show: (product) =>
        // Visualizar somente quando o produto tiver transação de entrada nele
        !product.isDeleted &&
        !product.is_asset &&
        product.categorias.length > 0 &&
        canAccess('Ver produtos'),
    },
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver produtos',
      onClick: (product) =>
        navigate(`/almoxarifado/produtos/detalhes/${product.id}`),
      show: (product) => !product.isDeleted && canAccess('Ver produtos'),
    },
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar produtos',
      onClick: (product) =>
        navigate(`/almoxarifado/produtos/editar/${product.id}`),
      show: (product) =>
        !product.isDeleted &&
        !product.is_in_transaction &&
        canAccess('Atualizar produtos'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir produtos',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateProductMutation.mutateAsync(product.id)
            } catch (error) {
              console.log('Erro ao excluir produto:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) =>
        !product.isDeleted &&
        !product.is_in_transaction &&
        canAccess('Excluir produtos'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir produtos',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateProductMutation.mutateAsync(product.id)
            } catch (error) {
              console.log('Erro ao restaurar produto:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) => product.isDeleted && canAccess('Excluir produtos'),
    },
  ]

  const { products = [], totalPages = 1 } = productsData || {}

  const columns = [
    { key: 'name', label: 'Nome' },
    {
      key: 'preco_medio',
      label: 'Preço Médio',
      numeric: true,
      isMoney: true,
      align: 'right',
    },
    { key: 'estoque', label: 'Estoque Atual', numeric: true, align: 'right' },
  ]

  const collapseConfig = {
    key: ['categorias'],
    totalField: ['categorias.preco_medio', 'categorias.estoque'],
    columns: [
      [
        { key: 'categorias.code', label: 'Código' },
        { key: 'categorias.categoria', label: 'Categoria' },
        { key: 'categorias.condicao', label: 'Condição' },
        { key: 'categorias.endereco', label: 'Endereço' },
        { key: 'categorias.localizacao', label: 'Localização' },
        {
          key: 'categorias.preco_medio',
          label: 'Preço Médio',
          numeric: true,
          isMoney: true,
          align: 'right',
        },
        {
          key: 'categorias.estoque',
          label: 'Estoque',
          numeric: true,
          align: 'right',
        },
      ],
    ],
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Produtos" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersProducts({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Produtos"
          buttonText="Cadastrar produto"
          buttonLink="/almoxarifado/produtos/criar"
          permission={'Criar produtos'}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={products}
          columns={columns}
          collapseConfig={collapseConfig}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="collapse"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem?.name || ''}
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default ProductsPage
